import { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    title: '',
    phone: '',
    email: '',
    location: '',
    equipmentType: '',
    timeline: '',
    estimatedValue: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qnvgk98t1oshnre0bg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          title: '',
          phone: '',
          email: '',
          location: '',
          equipmentType: '',
          timeline: '',
          estimatedValue: '',
          description: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Set page title
    document.title = "Contact ValveSalesSupply - Get Quote Industrial Equipment Texas Oklahoma Louisiana | Call 214-854-5638";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact ValveSalesSupply for quotes on surplus industrial equipment. Call 214-854-5638 or submit online for 24-hour response. Serving refineries and petrochemical plants across Texas, Oklahoma, Louisiana, and 6 more states.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'contact industrial equipment buyers, get equipment quote, sell surplus valves, industrial equipment quote Texas, refinery equipment buyers contact, ValveSalesSupply phone number');
    }

    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://valvesalessupply.com/contact');

    // Add Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Contact ValveSalesSupply - Get Quote for Industrial Equipment | 214-854-5638' },
      { property: 'og:description', content: 'Get professional quotes on surplus industrial equipment within 24 hours. Call 214-854-5638 or submit online. Serving 9 states.' },
      { property: 'og:url', content: 'https://valvesalessupply.com/contact' },
      { property: 'og:type', content: 'website' }
    ];

    ogTags.forEach(tag => {
      let meta = document.querySelector(`meta[property="${tag.property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', tag.property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', tag.content);
    });

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": "https://valvesalessupply.com/contact/#webpage",
          "url": "https://valvesalessupply.com/contact",
          "name": "Contact ValveSalesSupply - Get Quote for Industrial Equipment",
          "description": "Contact ValveSalesSupply for quotes on surplus industrial equipment. Call 214-854-5638 or submit your equipment list online for 24-hour response.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://valvesalessupply.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Contact",
                "item": "https://valvesalessupply.com/contact"
              }
            ]
          }
        },
        {
          "@type": "Organization",
          "@id": "https://valvesalessupply.com/#organization",
          "name": "ValveSalesSupply",
          "url": "https://valvesalessupply.com",
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+1-214-854-5638",
              "contactType": "customer service",
              "availableLanguage": "English",
              "hoursAvailable": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "17:00"
              }
            },
            {
              "@type": "ContactPoint",
              "contactType": "sales",
              "telephone": "+1-214-854-5638",
              "availableLanguage": "English"
            },
            {
              "@type": "ContactPoint",
              "contactType": "technical support",
              "telephone": "+1-214-854-5638",
              "availableLanguage": "English"
            }
          ],
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "TX",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 32.7767,
            "longitude": -96.7970
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "17:00"
            }
          ]
        },
        {
          "@type": "Service",
          "name": "Equipment Quote Service",
          "description": "Fast quotes on surplus industrial equipment with 24-hour response time",
          "provider": {
            "@id": "https://valvesalessupply.com/#organization"
          },
          "offers": {
            "@type": "Offer",
            "name": "Free Equipment Assessment",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Complimentary equipment evaluation and quote within 24 hours"
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How quickly will I receive a quote?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We respond within 24 hours with initial pricing and can provide detailed quotes within 48 hours of equipment assessment."
              }
            },
            {
              "@type": "Question",
              "name": "What information do I need to provide for a quote?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Provide equipment type, quantities, manufacturers, sizes, and condition. Photos and asset tags are helpful but not required for initial quotes."
              }
            },
            {
              "@type": "Question",
              "name": "Is there a minimum quantity for equipment purchases?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No minimum quantity required. We purchase everything from single items to complete plant shutdowns."
              }
            },
            {
              "@type": "Question",
              "name": "Do you provide on-site assessments?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we can arrange on-site assessments for large inventories or complex equipment. Contact us to schedule a visit."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-b from-[#1B3A5F] to-[#2B4A6F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-white">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">CONTACT US</h1>
          <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto">
            Get a professional quote for your surplus equipment within 24 hours. 
            Our experts are standing by to help you maximize your equipment value.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 mb-12 sm:mb-16">
            {[
              {
                icon: 'ri-phone-line',
                title: 'Call Us Direct',
                content: '(214) 854-5638',
                subtitle: '24/7 Emergency Response Available',
                action: 'tel:214-854-5638'
              },
              {
                icon: 'ri-mail-line',
                title: 'Email Us',
                content: 'valvesalessupply@gmail.com',
                subtitle: 'Response within 4 hours',
                action: 'mailto:valvesalessupply@gmail.com'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Service Area',
                content: 'TX, OK, LA, NM, KS, ND, SD, WY, UT',
                subtitle: '9 States Covered',
                action: null
              }
            ].map((contact, index) => (
              <div key={index} className="text-center">
                <div className="w-16 sm:w-20 h-16 sm:h-20 bg-[#1B3A5F] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <i className={`${contact.icon} text-white text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-[#1B3A5F] text-lg sm:text-xl font-bold mb-2">{contact.title}</h3>
                {contact.action ? (
                  <a
                    href={contact.action}
                    className="text-xl sm:text-2xl font-bold text-gray-800 hover:text-[#1B3A5F] transition-colors block mb-2"
                  >
                    {contact.content}
                  </a>
                ) : (
                  <p className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{contact.content}</p>
                )}
                <p className="text-gray-600 text-sm sm:text-base">{contact.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 sm:py-20 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-[#1B3A5F] text-3xl sm:text-4xl font-bold mb-4">REQUEST A QUOTE</h2>
            <p className="text-gray-600 text-lg sm:text-xl">
              Fill out the form below and we'll get back to you within 24 hours with a competitive quote.
            </p>
          </div>
          
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg">
            <form onSubmit={handleSubmit} data-readdy-form id="equipment-quote-form">
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    placeholder="Company or facility name"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="title" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Job Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    placeholder="e.g., Maintenance Manager"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    placeholder="(214) 854-5638"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    placeholder="your.email@company.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="location" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Facility Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    placeholder="City, State"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="equipmentType" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Equipment Type *
                  </label>
                  <select
                    id="equipmentType"
                    name="equipmentType"
                    value={formData.equipmentType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                  >
                    <option value="">Select equipment type</option>
                    <option value="valves">Valves</option>
                    <option value="instruments">Instruments & Gauges</option>
                    <option value="controls">Process Controls</option>
                    <option value="pumps">Pumps & Rotating Equipment</option>
                    <option value="safety">Safety Equipment</option>
                    <option value="analytical">Analytical Equipment</option>
                    <option value="mixed">Mixed Equipment Lot</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="timeline" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                    Removal Timeline
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate (Within 1 week)</option>
                    <option value="month">Within 1 month</option>
                    <option value="quarter">Within 3 months</option>
                    <option value="turnaround">During next turnaround</option>
                    <option value="flexible">Flexible timing</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-4 sm:mb-6">
                <label htmlFor="estimatedValue" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  Estimated Equipment Value
                </label>
                <select
                  id="estimatedValue"
                  name="estimatedValue"
                  value={formData.estimatedValue}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all text-sm sm:text-base"
                >
                  <option value="">Select value range</option>
                  <option value="under-10k">Under $10,000</option>
                  <option value="10k-50k">$10,000 - $50,000</option>
                  <option value="50k-100k">$50,000 - $100,000</option>
                  <option value="100k-500k">$100,000 - $500,000</option>
                  <option value="over-500k">Over $500,000</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              
              <div className="mb-6 sm:mb-8">
                <label htmlFor="description" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  Equipment Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A5F] focus:border-transparent outline-none transition-all resize-none text-sm sm:text-base"
                  placeholder="Please describe the equipment you want to sell, including quantities, sizes, manufacturers, conditions, etc. Include any specific requirements for removal or documentation."
                ></textarea>
                <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                  {formData.description.length}/500 characters
                </div>
              </div>
              
              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6 text-sm sm:text-base">
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    <span>Thank you! Your quote request has been submitted. We'll respond within 24 hours.</span>
                  </div>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm sm:text-base">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    <span>There was an error submitting your request. Please try again or call us directly.</span>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#1B3A5F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#2B4A6F] transition-all duration-300 flex items-center justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      SUBMIT QUOTE REQUEST
                      <i className="ri-arrow-right-line ml-2"></i>
                    </>
                  )}
                </button>
                
                <a
                  href="tel:214-854-5638"
                  className="border-2 border-[#1B3A5F] text-[#1B3A5F] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#1B3A5F] hover:text-white transition-all duration-300 flex items-center justify-center whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  OR CALL NOW
                </a>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Regional Contacts */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-[#1B3A5F] text-3xl sm:text-4xl font-bold mb-4">REGIONAL CONTACTS</h2>
            <p className="text-gray-600 text-lg sm:text-xl">Direct lines to our regional teams for faster service</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                region: 'Texas & Louisiana',
                states: 'TX, LA',
                phone: '(214) 854-5638',
                email: 'valvesalessupply@gmail.com',
                manager: 'Mark Driver',
                title: 'Regional Manager'
              },
              {
                region: 'Oklahoma & Kansas',
                states: 'OK, KS, NM',
                phone: '(214) 854-5638',
                email: 'valvesalessupply@gmail.com',
                manager: 'Mark Driver',
                title: 'Regional Manager'
              },
              {
                region: 'Northern Plains',
                states: 'ND, SD, WY, UT',
                phone: '(214) 854-5638',
                email: 'valvesalessupply@gmail.com',
                manager: 'Mark Driver',
                title: 'Regional Manager'
              }
            ].map((region, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-3xl p-6 sm:p-8">
                <h3 className="text-[#1B3A5F] text-xl sm:text-2xl font-bold mb-2">{region.region}</h3>
                <p className="text-[#6B7280] mb-4 sm:mb-6 text-sm sm:text-base">Serving: {region.states}</p>
                
                <div className="space-y-3 sm:space-y-4">
                  <a
                    href={`tel:${region.phone}`}
                    className="flex items-center text-[#1B3A5F] font-bold text-base sm:text-lg hover:underline"
                  >
                    <i className="ri-phone-line mr-3"></i>
                    {region.phone}
                  </a>
                  <a
                    href={`mailto:${region.email}`}
                    className="flex items-center text-gray-600 hover:text-[#1B3A5F] hover:underline text-sm sm:text-base break-all"
                  >
                    <i className="ri-mail-line mr-3 flex-shrink-0"></i>
                    {region.email}
                  </a>
                  <div className="pt-3 sm:pt-4 border-t border-gray-300">
                    <p className="font-semibold text-gray-800 text-sm sm:text-base">{region.manager}</p>
                    <p className="text-gray-600 text-xs sm:text-sm">{region.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-[#1B3A5F] text-3xl sm:text-4xl font-bold mb-4">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="text-gray-600 text-lg sm:text-xl">Common questions about contacting us and getting quotes</p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                question: 'How quickly will I receive a quote?',
                answer: 'We respond within 24 hours with initial pricing. For detailed quotes, we typically provide comprehensive pricing within 48 hours of equipment assessment. For urgent situations, we can expedite the process.'
              },
              {
                question: 'What information do I need to provide for a quote?',
                answer: 'Basic information includes equipment type, quantities, manufacturers, sizes, and general condition. Photos and asset tags are helpful but not required for initial quotes. The more details you provide, the more accurate our quote will be.'
              },
              {
                question: 'Is there a minimum quantity for equipment purchases?',
                answer: 'No minimum quantity required. We purchase everything from single high-value items to complete plant shutdowns. Every inquiry receives the same professional attention.'
              },
              {
                question: 'Do you provide on-site assessments?',
                answer: 'Yes, we can arrange on-site assessments for large inventories or complex equipment situations. Our experts will visit your facility to evaluate equipment and provide detailed quotes. Contact us to schedule a visit.'
              },
              {
                question: 'What are your business hours?',
                answer: 'Our office hours are Monday through Friday, 8:00 AM to 5:00 PM Central Time. However, we provide 24/7 emergency response for critical turnaround situations. Call our main line anytime for urgent needs.'
              },
              {
                question: 'Can I email photos of my equipment?',
                answer: 'Absolutely! Email photos to valvesalessupply@gmail.com along with any available details. Photos help us provide more accurate quotes and identify equipment specifications.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-md">
                <h3 className="text-[#1B3A5F] text-lg sm:text-xl font-bold mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 sm:py-20 bg-[#1B3A5F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="mb-6 sm:mb-8">
            <i className="ri-phone-line text-white text-4xl sm:text-6xl mb-4"></i>
          </div>
          <h2 className="text-white text-3xl sm:text-4xl font-bold mb-4">EMERGENCY TURNAROUND SUPPORT</h2>
          <p className="text-gray-200 text-lg sm:text-xl mb-6 sm:mb-8">
            Need equipment removed during an unplanned shutdown? We provide 24/7 emergency response 
            for critical turnaround situations.
          </p>
          
          <a
            href="tel:214-854-5638"
            className="bg-red-600 text-white px-8 sm:px-12 py-4 sm:py-6 rounded-full text-lg sm:text-2xl font-bold hover:bg-red-700 transition-all duration-300 inline-flex items-center whitespace-nowrap"
          >
            <i className="ri-phone-line mr-2 sm:mr-3"></i>
            EMERGENCY: (214) 854-5638
          </a>
          
          <p className="text-gray-300 text-base sm:text-lg mt-4 sm:mt-6">
            Available 24/7 for critical situations
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;